import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  TextField,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  LinkOff as UnmatchedIcon,
  AutoAwesome as AutoCreatedIcon,
  Link as MatchedIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tmsApi, customerApi } from '../../api/client';
import { TmsCustomerMapping, Customer } from '../../types';

const statusConfig: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'info'; icon: React.ReactNode }> = {
  MATCHED: { label: 'Matchet', color: 'success', icon: <MatchedIcon sx={{ fontSize: '0.8rem' }} /> },
  UNMATCHED: { label: 'Ikke matchet', color: 'warning', icon: <UnmatchedIcon sx={{ fontSize: '0.8rem' }} /> },
  AUTO_CREATED: { label: 'Auto-oprettet', color: 'info', icon: <AutoCreatedIcon sx={{ fontSize: '0.8rem' }} /> },
};

const confidenceColors: Record<string, 'success' | 'info' | 'default'> = {
  HIGH: 'success',
  MEDIUM: 'info',
  LOW: 'default',
};

export const TmsCustomerMappings: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tms-customer-mappings', statusFilter, page],
    queryFn: async () => {
      const params: any = { page, limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await tmsApi.getCustomerMappings(params);
      return res.data;
    },
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-for-mapping'],
    queryFn: async () => {
      const res = await customerApi.getAll({ limit: 500 });
      return res.data.data;
    },
  });

  const linkMutation = useMutation({
    mutationFn: ({ mappingId, customerId }: { mappingId: string; customerId: string }) =>
      tmsApi.linkCustomerMapping(mappingId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-customer-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setLinkingId(null);
      setSelectedCustomerId(null);
    },
  });

  const mappings: TmsCustomerMapping[] = data?.data || [];
  const pagination = data?.pagination;
  const customers: Customer[] = customersData || [];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={1.5} sx={{ fontSize: '1rem' }}>
        TMS Kundemapping
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: '0.75rem' }}>
        Her kan du koble Transpas kunde-ID'er til eksisterende CRM-kunder. Kunder markeret som
        "Ikke matchet" eller "Auto-oprettet" kraever gennemgang.
      </Typography>

      {/* Matching documentation accordion */}
      <Accordion
        sx={{
          mb: 2,
          '&:before': { display: 'none' },
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
          sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <HelpIcon sx={{ fontSize: '0.85rem', color: 'info.main' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              Saadan matcher systemet kunder
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
            Naar data synkroniseres fra Transpas og Kundedatabasen, forsoeger systemet automatisk at matche kunder i foelgende prioritetsraekkefoelge:
          </Typography>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.25 }}>
              1. Kundenummer (eksakt match)
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', pl: 1.5 }}>
              Transpas-kundenummer kobles direkte til CRM-kunden via kundemapping.
              Soerg for at kundenummeret i Transpas er korrekt registreret.
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.25 }}>
              2. CVR / Momsnummer (normaliseret match — hoej sikkerhed)
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', pl: 1.5 }}>
              Hvis kundenummeret ikke matcher, sammenlignes CVR/momsnummer.
              Systemet normaliserer formatet: "DE 811228366", "DE-811228366" og "DE811228366" behandles ens.
              <br />
              <strong>Soerg for at CVR/momsnummer er udfyldt BAADE i Transpas OG i CRM.</strong>
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.25 }}>
              3. Firmanavn (fuzzy match — forslag)
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', pl: 1.5 }}>
              Hvis hverken kundenummer eller CVR matcher, forsoeger systemet at matche paa firmanavn.
              Almindelige selskabsformer (A/S, GmbH, ApS, Ltd) og generiske ord (Transport, Logistics, Spedition)
              ignoreres. Navne-matches auto-linkes IKKE — de vises som forslag til manuel godkendelse.
            </Typography>
          </Box>

          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.5 }}>
              Statusforklaring
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              <strong>Matchet</strong> — Automatisk koblet via kundenummer eller CVR<br />
              <strong>Auto-oprettet</strong> — Ny CRM-kunde oprettet automatisk fra Transpas-data<br />
              <strong>Ikke matchet</strong> — Kraever manuel kobling (se forslag nedenfor)
            </Typography>
          </Box>

          <Box sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', borderRadius: 1, p: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.5 }}>
              Best practices
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              • Udfyld altid CVR/momsnummer i Transpas — det er den sikreste match<br />
              • Brug samme stavning af firmanavne i begge systemer<br />
              • Tjek "Ikke matchet" kunder efter hver sync og kobl dem manuelt<br />
              • Kunder med samme CVR grupperes automatisk i koncernhierarki
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box display="flex" gap={1} mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: '0.75rem' }}>Filtrer status</InputLabel>
          <Select
            value={statusFilter}
            label="Filtrer status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            sx={{ fontSize: '0.75rem' }}
          >
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Alle</MenuItem>
            <MenuItem value="UNMATCHED" sx={{ fontSize: '0.75rem' }}>Ikke matchet</MenuItem>
            <MenuItem value="AUTO_CREATED" sx={{ fontSize: '0.75rem' }}>Auto-oprettet</MenuItem>
            <MenuItem value="MATCHED" sx={{ fontSize: '0.75rem' }}>Matchet</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {mappings.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
          Ingen kundemappings fundet {statusFilter ? `med status "${statusConfig[statusFilter]?.label || statusFilter}"` : ''}.
        </Alert>
      ) : (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent sx={{ p: '8px !important' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Kunde</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'center' }}>Ventende</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>CRM Kunde</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping) => (
                    <TableRow key={mapping.id} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {mapping.tmsCustomerName || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                            Nr: {mapping.tmsCustomerId}
                          </Typography>
                          {mapping.tmsVatNumber && (
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                              CVR: {mapping.tmsVatNumber}
                            </Typography>
                          )}
                          {mapping.source && mapping.source !== 'transpas' && (
                            <Chip label={mapping.source} size="small" sx={{ fontSize: '0.5rem', height: 14 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig[mapping.status]?.icon as React.ReactElement}
                          label={statusConfig[mapping.status]?.label || mapping.status}
                          color={statusConfig[mapping.status]?.color || 'default'}
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 22 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {mapping.pendingBookingsCount > 0 && (
                            <Chip label={`${mapping.pendingBookingsCount} book.`} color="warning" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />
                          )}
                          {mapping.pendingOffersCount > 0 && (
                            <Chip label={`${mapping.pendingOffersCount} tilb.`} color="warning" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />
                          )}
                          {!mapping.pendingBookingsCount && !mapping.pendingOffersCount && (
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem' }}>
                        {mapping.customer ? mapping.customer.name : '-'}
                      </TableCell>
                      <TableCell>
                        {(mapping.status === 'UNMATCHED' || mapping.status === 'AUTO_CREATED') && (
                          linkingId === mapping.id ? (
                            <Box display="flex" gap={0.5} alignItems="center">
                              <Autocomplete
                                size="small"
                                options={customers}
                                getOptionLabel={(c) => `${c.name}${c.cvr ? ` (${c.cvr})` : ''}`}
                                onChange={(_, value) => setSelectedCustomerId(value?.id || null)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Vaelg kunde..." sx={{ minWidth: 200, '& input': { fontSize: '0.7rem' } }} />
                                )}
                                sx={{ '& .MuiAutocomplete-option': { fontSize: '0.7rem' } }}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!selectedCustomerId || linkMutation.isPending}
                                onClick={() => selectedCustomerId && linkMutation.mutate({ mappingId: mapping.id, customerId: selectedCustomerId })}
                                sx={{ fontSize: '0.65rem', minWidth: 60 }}
                              >
                                {linkMutation.isPending ? '...' : 'Link'}
                              </Button>
                              <Button
                                size="small"
                                onClick={() => { setLinkingId(null); setSelectedCustomerId(null); }}
                                sx={{ fontSize: '0.65rem' }}
                              >
                                Annuller
                              </Button>
                            </Box>
                          ) : (
                            <Box display="flex" gap={0.5} alignItems="center">
                              {mapping.suggestedCustomer && (
                                <Tooltip title={mapping.matchReason || 'Foreslaaet match'}>
                                  <Chip
                                    label={`${mapping.suggestedCustomer.name}`}
                                    color={confidenceColors[mapping.matchConfidence || 'LOW']}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 22, maxWidth: 160 }}
                                    onDelete={() => {
                                      if (mapping.suggestedCustomerId) {
                                        linkMutation.mutate({ mappingId: mapping.id, customerId: mapping.suggestedCustomerId });
                                      }
                                    }}
                                    deleteIcon={<CheckIcon sx={{ fontSize: '0.7rem !important' }} />}
                                  />
                                </Tooltip>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setLinkingId(mapping.id)}
                                sx={{ fontSize: '0.65rem', borderRadius: 1 }}
                              >
                                Kobl til kunde
                              </Button>
                            </Box>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Forrige
                </Button>
                <Typography sx={{ fontSize: '0.7rem', lineHeight: '30px' }}>
                  Side {page} af {pagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Naeste
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TmsCustomerMappings;
